import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Globe2, Loader2, Clock, FileText, CheckCircle2, AlertTriangle,
  MapPin, Eye, EyeOff, Trash2, Info, ImageIcon,
} from "lucide-react";
import { setOrgTz, fmtLog } from "@/lib/tz";

const CRIMSON = "var(--brand)";
const TEAL = "#2f6f6b";

// ── Branding: the look as Fully Funded Shifts set it in the head app (read-only here) ──────────
function BrandImageRow({ slot, title, hint, boxClass, hasKey, status }) {
  const [url, setUrl] = useState(null);
  const blob = slot === "icon" ? api.brandingIconBlob : api.brandingLogoBlob;
  const has = status?.[hasKey];

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      if (!has) { setUrl(null); return; }
      try {
        const b = await blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(b);
        setUrl(objectUrl);
      } catch { /* leave as is */ }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [has]);

  return (
    <div className="mb-3" data-testid={`branding-${slot}-row`}>
      <div className="text-xs font-semibold text-slate-700">{title}</div>
      <p className="text-[11px] text-slate-500 mb-1.5">{hint}</p>
      <div className={`${boxClass} flex items-center justify-center bg-white border border-slate-200 rounded`}>
        {url ? <img src={url} alt={`Current ${title.toLowerCase()}`} className="max-h-full max-w-full object-contain p-1" data-testid={`branding-${slot}-preview`} />
          : <span className="text-[11px] text-slate-400">{status ? "The built-in one" : <Loader2 className="h-4 w-4 animate-spin" />}</span>}
      </div>
    </div>
  );
}

function ThemeSummary({ theme }) {
  if (!theme) return <p className="text-[11px] text-slate-500 mb-3" data-testid="branding-theme">Colours and fonts: the built-in look.</p>;
  return (
    <div className="mb-3 text-[11px] text-slate-600" data-testid="branding-theme">
      <div><span className="font-semibold text-slate-700">App name:</span> {theme.app_name} (texts and authenticator apps show {theme.short_name})</div>
      <div className="flex items-center gap-2 mt-1">
        <span className="font-semibold text-slate-700">Colours:</span>
        {Object.entries(theme.colours).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-slate-300" style={{ backgroundColor: v }} />{k} {v}</span>
        ))}
      </div>
      <div className="mt-1"><span className="font-semibold text-slate-700">Fonts:</span> {theme.fonts.heading} for headings, {theme.fonts.body} for text</div>
    </div>
  );
}

function BrandingCard() {
  const [status, setStatus] = useState(null); // { has_logo, has_icon, theme }
  const [restyle, setRestyle] = useState(null); // { dry_run, agreements, documents, errors }
  const [restyleBusy, setRestyleBusy] = useState(false);

  const loadStatus = async () => {
    try {
      setStatus(await api.brandingStatus());
    } catch { /* leave as is */ }
  };
  useEffect(() => { loadStatus(); }, []);

  const runRestyle = async (dryRun) => {
    setRestyleBusy(true);
    try { setRestyle(await api.restyleBrandedPdfs(dryRun)); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't restyle the PDFs."); }
    finally { setRestyleBusy(false); }
  };

  const skipped = restyle ? restyle.agreements.skipped.length + restyle.documents.skipped.length : 0;
  const count = restyle ? restyle.agreements.restyled + restyle.documents.restyled : 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="branding-card">
      <div className="flex items-center gap-2 mb-1">
        <ImageIcon className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold font-brand text-slate-800">Branding</h3>
      </div>
      <p className="text-[11px] text-slate-500 mb-3" data-testid="branding-set-by">
        Your logo, icon, colours and fonts are set by Fully Funded Shifts. To change them, contact Fully Funded
        Shifts support. They appear on generated PDFs (service agreements, care plans and reports) and throughout
        the app: sign-in, the header and the participant signing page.
      </p>
      {status && <ThemeSummary theme={status.theme} />}
      <BrandImageRow slot="logo" title="Logo" hasKey="has_logo" status={status} boxClass="h-16 w-40"
        hint="Wide logo shown on PDFs, the login page, the app header and the signing page." />
      <BrandImageRow slot="icon" title="Icon" hasKey="has_icon" status={status} boxClass="h-16 w-16"
        hint="Square symbol for tight spaces such as the worker app header. Without one, the logo is used there." />

      <div className="border-t border-slate-100 pt-3">
        <h4 className="text-xs font-semibold text-slate-700 mb-1">Update existing PDFs</h4>
        <p className="text-[11px] text-slate-500 mb-3">
          Signed service agreements and approved care plans keep the PDF they were created with. This re-creates
          those PDFs with the current logo and branding. Wording, signatures and dates are not changed: each new
          PDF is compared with the original and only used if no text differs, and the original is kept.
          Documents uploaded from other systems are never touched.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => runRestyle(true)} disabled={restyleBusy} data-testid="branding-restyle-preview">
            {restyleBusy && restyle === null ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Check what would change
          </Button>
          {restyle?.dry_run && count > 0 && (
            <Button size="sm" className="h-9 text-xs" style={{ backgroundColor: CRIMSON }} onClick={() => runRestyle(false)} disabled={restyleBusy} data-testid="branding-restyle-apply">
              {restyleBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Update {count} PDF{count === 1 ? "" : "s"}
            </Button>
          )}
        </div>
        {restyle && (
          <div className={`mt-3 rounded-lg border p-3 text-xs ${skipped || restyle.errors ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="branding-restyle-result">
            <div className="font-semibold mb-1.5 text-slate-800">{restyle.dry_run ? "Check complete (nothing changed yet)" : "PDFs updated"}</div>
            <ul className="space-y-0.5 text-slate-600 pl-5 list-disc">
              <li>Service agreements {restyle.dry_run ? "ready to update" : "updated"}: <strong>{restyle.agreements.restyled}</strong></li>
              <li>Care plans and documents {restyle.dry_run ? "ready to update" : "updated"}: <strong>{restyle.documents.restyled}</strong></li>
              {skipped > 0 && <li className="text-amber-700">Left as they are because the new PDF would not match the original text: <strong>{skipped}</strong></li>}
              {restyle.errors > 0 && <li className="text-amber-700">Couldn&apos;t be processed: <strong>{restyle.errors}</strong>. Check the server logs.</li>}
            </ul>
            {skipped > 0 && (
              <ul className="mt-1.5 pl-5 list-disc text-[11px] text-slate-500">
                {[...restyle.agreements.skipped, ...restyle.documents.skipped].slice(0, 8).map((x) => (
                  <li key={x.id}>{x.name || x.id}: {x.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Maps / Address provider card ─────────────────────────────────────────────
function MapsCard({
  mapsProvider, setMapsProvider, mapsProviderSaved,
  googleKeySet, googleKeyInput, setGoogleKeyInput,
  showKey, setShowKey,
  mapsBusy, deletingKey,
  saveMaps, deleteKey,
}) {
  const isChanged = mapsProvider !== mapsProviderSaved || googleKeyInput.trim() !== "";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="maps-card">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold font-brand text-slate-800">Address search provider</h3>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Controls the autocomplete backend used when typing an address on staff records,
        participant profiles, and shift locations. OpenStreetMap (Nominatim) is free but
        has a strict rate limit — if the dropdown isn't showing results, switch to Google Maps.
      </p>

      {/* Provider toggle */}
      <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-slate-700">
            {mapsProvider === "google" ? "Google Maps (Places API)" : "OpenStreetMap (Nominatim)"}
          </span>
          <Badge className={
            mapsProvider === "google"
              ? "text-[9px] py-0 bg-blue-50 text-blue-700 border-blue-200 border"
              : "text-[9px] py-0 bg-slate-100 text-slate-500 border-slate-200 border"
          }>
            {mapsProvider === "google" ? "Google" : "Free / Open"}
          </Badge>
          {mapsProviderSaved === "google" && googleKeySet && (
            <Badge className="text-[9px] py-0 bg-emerald-50 text-emerald-700 border-emerald-200 border">
              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5 inline" />Active
            </Badge>
          )}
        </div>
        <Switch
          data-testid="maps-provider-toggle"
          checked={mapsProvider === "google"}
          onCheckedChange={(v) => setMapsProvider(v ? "google" : "nominatim")}
        />
      </div>

      {/* Google API key — only visible when Google is selected */}
      {mapsProvider === "google" && (
        <div className="space-y-2 mb-3" data-testid="google-key-section">
          <label className="text-[11px] font-medium text-slate-600">
            Google Maps API key <span className="text-rose-500">*</span>
          </label>

          {googleKeySet && !googleKeyInput ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 font-mono tracking-wider">
                ••••••••••••••••••••••••••••••••••••••••
              </div>
              <Button size="sm" variant="outline"
                className="h-8 text-xs text-slate-500 border-slate-200"
                onClick={() => setGoogleKeyInput(" ")} data-testid="maps-key-replace">
                Replace
              </Button>
              <Button size="sm" variant="outline"
                className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={deleteKey} disabled={deletingKey} data-testid="maps-key-delete">
                {deletingKey ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  data-testid="maps-key-input"
                  type={showKey ? "text" : "password"}
                  value={googleKeyInput.trimStart()}
                  onChange={(e) => setGoogleKeyInput(e.target.value)}
                  placeholder="AIza…"
                  className="h-9 text-xs pr-8 font-mono"
                  autoComplete="off"
                />
                <button type="button" onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}>
                  {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              {googleKeySet && (
                <Button size="sm" variant="ghost" className="h-9 text-xs text-slate-400"
                  onClick={() => setGoogleKeyInput("")} data-testid="maps-key-cancel-replace">
                  Cancel
                </Button>
              )}
            </div>
          )}

          <div className="flex items-start gap-1.5 rounded-md bg-blue-50 border border-blue-100 px-2.5 py-2 text-[10px] text-blue-700">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              Requires a Google Cloud project with the <strong>Places API</strong> enabled and
              an API key with <em>Places API</em> access. The key is stored securely server-side and
              never exposed in the browser.{" "}
              <a href="https://developers.google.com/maps/documentation/places/web-service/get-api-key"
                target="_blank" rel="noopener noreferrer" className="underline font-medium">
                Get an API key →
              </a>
            </span>
          </div>
        </div>
      )}

      <Button size="sm" onClick={saveMaps}
        disabled={mapsBusy || !isChanged}
        data-testid="maps-save" style={{ backgroundColor: TEAL }} className="h-8 text-xs font-semibold">
        {mapsBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
        Save maps settings
      </Button>
    </div>
  );
}

export default function GeneralSettings() {
  const [tz, setTz] = useState("");
  const [options, setOptions] = useState([]);
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Maps provider state
  const [mapsProvider, setMapsProvider] = useState("nominatim");
  const [mapsProviderSaved, setMapsProviderSaved] = useState("nominatim");
  const [googleKeySet, setGoogleKeySet] = useState(false);
  const [googleKeyInput, setGoogleKeyInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [mapsBusy, setMapsBusy] = useState(false);
  const [deletingKey, setDeletingKey] = useState(false);

  useEffect(() => {
    api.generalSettings().then((g) => {
      setTz(g.timezone);
      setSaved(g.timezone);
      setOptions(g.options || []);
      setMapsProvider(g.maps_provider || "nominatim");
      setMapsProviderSaved(g.maps_provider || "nominatim");
      setGoogleKeySet(!!g.google_maps_api_key_set);
    }).catch(() => toast.error("Couldn't load settings."));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.saveGeneralSettings({ timezone: tz });
      setOrgTz(r.timezone);
      setSaved(r.timezone);
      toast.success("Timezone updated — log times now display in " + r.timezone);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the timezone.");
    } finally { setBusy(false); }
  };

  const saveMaps = async () => {
    if (mapsProvider === "google" && !googleKeySet && !googleKeyInput.trim()) {
      toast.error("Enter a Google Maps API key before switching to Google provider.");
      return;
    }
    setMapsBusy(true);
    try {
      const payload = {
        timezone: saved, // required field — send current saved value unchanged
        maps_provider: mapsProvider,
        ...(googleKeyInput.trim() ? { google_maps_api_key: googleKeyInput.trim() } : {}),
      };
      const r = await api.saveGeneralSettings(payload);
      setMapsProviderSaved(r.maps_provider || "nominatim");
      setMapsProvider(r.maps_provider || "nominatim");
      setGoogleKeySet(!!r.google_maps_api_key_set);
      setGoogleKeyInput("");
      toast.success("Maps provider settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save maps settings.");
    } finally { setMapsBusy(false); }
  };

  const deleteKey = async () => {
    if (!window.confirm(
      "Remove the stored Google Maps API key?\n\n" +
      "Address search will fall back to OpenStreetMap (Nominatim) immediately."
    )) return;
    setDeletingKey(true);
    try {
      await api.deleteGoogleMapsKey();
      setGoogleKeySet(false);
      setMapsProvider("nominatim");
      setMapsProviderSaved("nominatim");
      setGoogleKeyInput("");
      toast.success("Google Maps API key removed — address search is now using Nominatim.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove the key.");
    } finally { setDeletingKey(false); }
  };

  const runBackfill = async () => {
    if (!window.confirm(
      "This will generate and store signed PDFs for all Active/Archived service agreements " +
      "that were built in this system and don't yet have a PDF.\n\n" +
      "Uploaded/legacy agreements from other systems are excluded.\n\n" +
      "This may take a moment if there are many agreements. Continue?"
    )) return;
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const r = await api.saBackfillPdfs();
      setBackfillResult(r);
      if (r.errors > 0) {
        toast.warning(`Backfill complete — ${r.generated} generated, ${r.errors} failed.`);
      } else {
        toast.success(`Backfill complete — ${r.generated} PDF${r.generated !== 1 ? "s" : ""} generated.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed.");
    } finally { setBackfillBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="general-settings">
      <div className="flex items-center gap-2.5 mb-1">
        <Globe2 className="h-5 w-5" style={{ color: CRIMSON }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">General Settings</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Organisation-wide display preferences. System and audit timestamps (activity logs, audit
        trail, document views, reminder emails) are recorded in UTC and shown in the timezone you
        pick here — handy when the service operates across states.
      </p>

      {/* ── Timezone ── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="tz-card">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold font-brand text-slate-800">Display timezone</h3>
        </div>
        <Select value={tz} onValueChange={setTz}>
          <SelectTrigger className="w-full text-sm" data-testid="tz-select">
            <SelectValue placeholder="Select a timezone" />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o} value={o} data-testid={`tz-option-${o}`}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="mt-3 text-[11px] text-slate-500" data-testid="tz-preview">
          Right now a log entry stamped this second would read: <span className="font-medium text-slate-700">{fmtLog(new Date().toISOString())}</span>
        </div>
        <Button onClick={save} disabled={busy || !tz || tz === saved} data-testid="tz-save"
          className="mt-3 h-9 text-xs font-semibold" style={{ backgroundColor: CRIMSON }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save timezone"}
        </Button>
      </div>

      {/* ── Maps / Address provider ── */}
      <MapsCard
        mapsProvider={mapsProvider} setMapsProvider={setMapsProvider}
        mapsProviderSaved={mapsProviderSaved}
        googleKeySet={googleKeySet} googleKeyInput={googleKeyInput}
        setGoogleKeyInput={setGoogleKeyInput}
        showKey={showKey} setShowKey={setShowKey}
        mapsBusy={mapsBusy} deletingKey={deletingKey}
        saveMaps={saveMaps} deleteKey={deleteKey}
      />

      <BrandingCard />

      {/* ── SA PDF Migration ── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sa-backfill-card">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold font-brand text-slate-800">Service Agreement PDF migration</h3>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          One-time migration for agreements signed before PDF generation was introduced. Generates
          and stores a signed PDF for any <strong>Active</strong> or <strong>Archived</strong> service
          agreement that was built in this system but does not yet have a PDF on record.
          <br /><br />
          <strong>Only agreements built in My Prydes Support are processed.</strong> Agreements uploaded
          from external systems (ShiftCare, FlowLogic, Good Human, etc.) are always excluded — they
          already have their own attached file. Safe to run multiple times — already-generated PDFs
          are never overwritten.
        </p>
        <Button size="sm" onClick={runBackfill} disabled={backfillBusy}
          style={{ backgroundColor: TEAL }} data-testid="sa-backfill-btn">
          {backfillBusy
            ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Generating PDFs…</>
            : <><FileText className="h-4 w-4 mr-1.5" />Generate missing signed PDFs</>}
        </Button>
        {backfillResult && (
          <div className={`mt-3 rounded-lg border p-3 text-xs ${backfillResult.errors > 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="sa-backfill-result">
            <div className="flex items-center gap-1.5 font-semibold mb-1.5">
              {backfillResult.errors > 0
                ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              <span className={backfillResult.errors > 0 ? "text-amber-800" : "text-emerald-800"}>Backfill complete</span>
            </div>
            <ul className="space-y-0.5 text-slate-600 pl-5 list-disc">
              <li>Eligible agreements found: <strong>{backfillResult.total}</strong></li>
              <li>PDFs generated: <strong>{backfillResult.generated}</strong></li>
              {backfillResult.skipped > 0 && <li>Skipped (no agreement content): <strong>{backfillResult.skipped}</strong></li>}
              {backfillResult.errors > 0 && (
                <li className="text-amber-700">Failed (rendering error): <strong>{backfillResult.errors}</strong> — check server logs for details</li>
              )}
            </ul>
            {backfillResult.generated === 0 && backfillResult.errors === 0 && (
              <p className="text-slate-500 mt-1.5 italic">Nothing to do — all eligible agreements already have a PDF stored.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

